/**
 * © 2025 Malvys Exaucé Dessou — ZonePro. Tous droits réservés.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, zoningAPI, analysisAPI, adminAPI } from '../api';
import { useLanguage } from '../context/LanguageContext';
import api from '../api';
import SteThereseZoneSheet from '../components/SteThereseZoneSheet';
import ReadZoneSheet from '../components/ReadZoneSheet';

// Affichage des champs de la fiche officielle : distingue « non prescrit » d'une valeur absente
const ficheVal = (field, suffix = '') => {
  if (!field) return '—';
  if (field.value !== null && field.value !== undefined) return `${field.value}${suffix}`;
  return field.status === 'non_prescrit' ? 'non prescrit' : '—';
};

// A list of usage codes, each as the API serves it in usage_display (spec 124).
const usagesVal = (field, display) => (
  Array.isArray(field?.value) ? field.value.map((code) => display?.[code] ?? code).join(' ; ') : ficheVal(field)
);

// Vrai si la fiche officielle ne prescrit AUCUNE norme dimensionnelle (cas des zones E.1 — parcs/institutionnel)
const sheetHasNoDimensionalNorms = (sheet) => {
  const ds = sheet?.dimensional_standards;
  if (!ds) return false;
  const fields = [
    ds.hauteur_m?.min, ds.hauteur_m?.max, ds.hauteur_etage?.min, ds.hauteur_etage?.max,
    ds.taux_implantation_pct?.min, ds.taux_implantation_pct?.max, ds.densite?.min, ds.densite?.max,
    ds.marge_laterale_min_m, ds.marge_arriere_min_m,
  ];
  return fields.every((f) => f?.value === null || f?.value === undefined);
};

const MODE_IMPLANTATION_LABELS = { I: 'Isolé', J: 'Jumelé', C: 'Contigu', RI: "Règlement d'insertion" };
const modeImplantationLabel = (field) => {
  const v = field?.value;
  if (!v) return field?.status === 'non_prescrit' ? 'non prescrit' : '—';
  return v.split(',').map((c) => {
    const code = c.trim();
    return MODE_IMPLANTATION_LABELS[code] ? `${MODE_IMPLANTATION_LABELS[code]} (${code})` : code;
  }).join(', ');
};

// ---- Sainte-Thérèse : composants (même structure d'affichage que Rosemont) ----

// Bannière de provenance de la zone (géométrie dérivée JMap ou saisie manuelle)
const StProvenanceBanner = ({ property }) => {
  const zr = property?.zone_resolution;
  if (zr?.method === 'derived_geometry') {
    return (
      <StAlertLazy testid="st-provenance-banner" tone="teal" title={`Provenance de la zone ${property?.zone_code || ''}`}>
        Zone déterminée par géométrie extraite de la carte interactive officielle de la Ville (JMap).
        Concordance vérifiée contre la carte officielle : 100 % à l&apos;intérieur des zones (45/45) et
        98,6 % aux abords des limites (73/74, aucun écart réel). Mesure du 10 août 2026 (échantillon de
        119 points). En attente du fichier géomatique officiel de la Ville.
        {zr.chosen_from_options && (
          <> Le terrain touchait une limite de zones — zone confirmée par l&apos;utilisateur parmi les options détectées.</>
        )}
      </StAlertLazy>
    );
  }
  return (
    <StAlertLazy testid="st-manual-zone-note" tone="muted" title="Zone saisie manuellement (repli)">
      Le code de zone de ce projet a été saisi manuellement, sans résolution géométrique. Vérifiable sur la
      carte officielle : <a href="https://carte.sainte-therese.ca/zonage" target="_blank" rel="noopener noreferrer" className="underline">carte.sainte-therese.ca/zonage</a>
    </StAlertLazy>
  );
};

// A zone placed from the plan of the règlement, not a published map: the warning the service derived (spec 148)
const ZoneOutlinesWarning = ({ property }) => {
  const warning = property?.zone_outlines?.warning;
  if (!warning) return null;
  return (
    <Alert className="mb-4 border-amber-300 bg-amber-50" data-testid="zone-outlines-warning">
      <AlertTriangle className="h-4 w-4 text-amber-700" />
      <AlertTitle className="text-amber-900">{`Zone ${property?.zone_code || ''} : ${property.zone_outlines.source}`}</AlertTitle>
      <AlertDescription className="text-amber-800">{warning}</AlertDescription>
    </Alert>
  );
};

const StAlertLazy = ({ testid, tone, title, children }) => (
  <Alert
    className={tone === 'teal' ? 'border-teal-300 bg-teal-50' : 'bg-muted/40'}
    data-testid={testid}
  >
    <Info className={`h-4 w-4 ${tone === 'teal' ? 'text-teal-700' : ''}`} />
    <AlertTitle className={tone === 'teal' ? 'text-teal-900' : ''}>{title}</AlertTitle>
    <AlertDescription className={tone === 'teal' ? 'text-teal-800' : ''}>{children}</AlertDescription>
  </Alert>
);

// Données cadastrales — mêmes conventions que la carte Montréal (Cadastre du Québec)
const StCadastralCard = ({ property }) => (
  <Card data-testid="st-cadastral-card">
    <CardHeader>
      <CardTitle className="font-heading uppercase flex items-center gap-2">
        <FileText className="w-5 h-5 text-accent" />
        Données cadastrales
      </CardTitle>
      <CardDescription className="flex items-center gap-2">
        Informations du lot issues du Cadastre du Québec (MERN)
        <span className="w-2 h-2 rounded-full bg-green-500" title="Confirmé"></span>
      </CardDescription>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <div>
          <div className="flex items-center gap-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Numéro de lot</p>
            <span className="w-2 h-2 rounded-full bg-green-500" title="Confirmé - Cadastre du Québec"></span>
          </div>
          <p className="font-mono text-xl font-bold text-primary">{property?.cadastral_number || '—'}</p>
          <p className="text-xs text-muted-foreground">Cadastre du Québec</p>
        </div>
        <div>
          <div className="flex items-center gap-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Superficie</p>
            <span className="w-2 h-2 rounded-full bg-green-500" title="Confirmé - Géométrie cadastrale"></span>
          </div>
          <p className="text-xl font-bold">{property?.lot_area?.toFixed(1) || '—'} m²</p>
          <p className="text-xs text-muted-foreground">Géométrie cadastrale</p>
        </div>
        <div>
          <div className="flex items-center gap-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Largeur (façade)</p>
            <span className={`w-2 h-2 rounded-full ${
              property?.dimension_confidence === 'high' ? 'bg-green-500' :
              property?.dimension_confidence === 'medium' ? 'bg-yellow-500' : 'bg-gray-400'
            }`}></span>
          </div>
          <p className="text-xl font-bold">{property?.lot_width?.toFixed(2) || '—'} m</p>
          <p className="text-xs text-muted-foreground">Calcul MBR</p>
        </div>
        <div>
          <div className="flex items-center gap-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Profondeur</p>
            <span className={`w-2 h-2 rounded-full ${
              property?.dimension_confidence === 'high' ? 'bg-green-500' :
              property?.dimension_confidence === 'medium' ? 'bg-yellow-500' : 'bg-gray-400'
            }`}></span>
          </div>
          <p className="text-xl font-bold">{property?.lot_depth?.toFixed(2) || '—'} m</p>
          <p className="text-xs text-muted-foreground">Calcul MBR</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const StLocationCard = ({ property }) => (
  <Card data-testid="st-location-card">
    <CardHeader>
      <CardTitle className="font-heading uppercase flex items-center gap-2">
        <MapPin className="w-5 h-5 text-accent" />
        Localisation
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <div className="flex items-center gap-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Municipalité</p>
            <span className="w-2 h-2 rounded-full bg-green-500" title="Confirmé"></span>
          </div>
          <p className="text-lg font-semibold">Sainte-Thérèse</p>
          <p className="text-xs text-muted-foreground">MRC Thérèse-De Blainville</p>
        </div>
        <div>
          <div className="flex items-center gap-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Coordonnées</p>
            <span className={`w-2 h-2 rounded-full ${property?.latitude ? 'bg-green-500' : 'bg-gray-400'}`}></span>
          </div>
          <p className="font-mono text-sm">
            {property?.latitude?.toFixed(6) || '—'}, {property?.longitude?.toFixed(6) || '—'}
          </p>
          <p className="text-xs text-muted-foreground">{property?.latitude ? 'Point utilisé pour la résolution de zone' : 'Non disponible (saisie manuelle)'}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Profil de propriété : absence explicite (rôle MAMH non intégré), pas des champs vides
const StPropertyProfileCard = () => (
  <Card data-testid="st-property-profile-note">
    <CardHeader>
      <CardTitle className="font-heading uppercase flex items-center gap-2">
        <Home className="w-5 h-5 text-accent" />
        Profil de propriété
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="text-center py-6 text-muted-foreground">
        <Home className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="font-medium">Profil de propriété non disponible pour cette municipalité pour l&apos;instant</p>
        <p className="text-sm mt-1">
          Le rôle d&apos;évaluation foncière (MAMH) de Sainte-Thérèse n&apos;est pas encore intégré — matricule,
          superficie du bâtiment, année de construction et nombre de logements seront ajoutés lors de son ingestion.
        </p>
      </div>
    </CardContent>
  </Card>
);
import { 
  ArrowLeft, FileText, Upload, Play, Download, Building, MapPin, Ruler,
  AlertTriangle, CheckCircle2, HelpCircle, BarChart3, Trash2,
  DollarSign, Leaf, TrendingUp, Users, ExternalLink, Gauge, Home,
  Database, Layers, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import ZoneSelector from '../components/ZoneSelector';
import NormeDot from '../components/NormeDot';
import { RULE_DOTS } from '../lib/ruleDots';
import { RuleValueWithStatus, RuleStatusSummary, StatusLegend } from '../components/RuleStatus';

// Component for "Coming Soon" tabs
const ComingSoonTab = ({ title, description, icon: Icon }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <div className="w-20 h-20 rounded-full bg-muted/50 flex items-center justify-center mb-6">
      <Icon className="w-10 h-10 text-muted-foreground" />
    </div>
    <h3 className="text-xl font-heading font-bold text-center mb-2">{title}</h3>
    <p className="text-muted-foreground text-center max-w-md mb-6">{description}</p>
    <Badge variant="secondary" className="text-sm px-4 py-2">
      Bientôt disponible
    </Badge>
  </div>
);

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();  
  // Translated labels
  const scenarioLabels = {
    // New construction scenarios
    conservative: { 
      name: language === 'fr' ? 'Construction prudente' : 'Conservative construction', 
      color: 'warning', 
      desc: language === 'fr' ? 'Interprétation prudente des règlements' : 'Conservative interpretation of regulations', 
      category: 'new_construction' 
    },
    standard: { 
      name: language === 'fr' ? 'Construction typique' : 'Standard construction', 
      color: 'accent', 
      desc: language === 'fr' ? 'Scénario de développement réaliste' : 'Realistic development scenario', 
      category: 'new_construction' 
    },
    optimized: { 
      name: language === 'fr' ? 'Construction maximale' : 'Maximum construction', 
      color: 'success', 
      desc: language === 'fr' ? "Potentiel maximal selon l'interprétation" : 'Maximum potential based on interpretation', 
      category: 'new_construction' 
    },
    // Existing building scenarios
    vertical_addition: { 
      name: language === 'fr' ? 'Surélévation' : 'Vertical addition', 
      color: 'info', 
      desc: language === 'fr' ? "Ajout d'étage(s) sur structure existante" : 'Adding floor(s) to existing structure', 
      category: 'existing_building' 
    },
    demolition_reconstruction: { 
      name: language === 'fr' ? 'Démolition-reconstruction' : 'Demolition & reconstruction', 
      color: 'warning', 
      desc: language === 'fr' ? 'Démolition complète et construction neuve' : 'Complete demolition and new construction', 
      category: 'existing_building' 
    },
    partial_renovation: { 
      name: language === 'fr' ? 'Rénovation partielle' : 'Partial renovation', 
      color: 'muted', 
      desc: language === 'fr' ? 'Rénovation et optimisation du bâtiment existant' : 'Renovation and optimization of existing building', 
      category: 'existing_building' 
    }
  };

  const permitStatusLabels = {
    approved: { label: language === 'fr' ? 'Approuvé' : 'Approved', color: 'bg-green-100 text-green-800' },
    under_review: { label: language === 'fr' ? 'En révision' : 'Under review', color: 'bg-yellow-100 text-yellow-800' },
    rejected: { label: language === 'fr' ? 'Refusé' : 'Rejected', color: 'bg-red-100 text-red-800' }
  };
  
  const [project, setProject] = useState(null);
  const isSteTherese = project?.property?.municipality === 'Sainte-Thérèse';
  // a Montréal arrondissement whose unit is the zone, read by the www1-fiche reader (spec 127)
  const readZone = !isSteTherese && project?.property?.jurisdiction_id && project?.property?.zone_code
    && !project?.property?.sector_number;
  const [zoningRule, setZoningRule] = useState(null);
  const [zoneTemplates, setZoneTemplates] = useState([]);
  const [sectorSheet, setSectorSheet] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showArticleDialog, setShowArticleDialog] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const [zoningForm, setZoningForm] = useState({
    permitted_uses: [],
    max_height_meters: '',
    max_height_storeys: '',
    front_setback: '',
    side_setback_left: '',
    side_setback_right: '',
    rear_setback: '',
    lot_coverage_percent: '',
    floor_area_ratio: '',
    parking_ratio: '',
    special_constraints: '',
    notes: '',
    field_status: {},
    zone_code_source: ''
  });

  const [zoningInputMode, setZoningInputMode] = useState('template'); // 'template' or 'manual'

  const [financialInputs, setFinancialInputs] = useState({
    land_price: '',
    construction_cost_per_sqm: '',
    sale_price_per_sqm: '',
    rental_value_per_unit: ''
  });

  const loadProject = useCallback(async () => {
    try {
      const projectRes = await projectsAPI.get(id);
      setProject(projectRes.data);
      
      // Fiche officielle complète du secteur (toutes les données extraites)
      if (projectRes.data.property?.sector_number) {
        try {
          const sheetRes = await api.get(`/regulations/sector/${projectRes.data.property.sector_number}`);
          setSectorSheet(sheetRes.data);
        } catch (e) {
          console.debug('Fiche secteur non trouvée');
        }
      }
      
      try {
        const zoningRes = await zoningAPI.get(id);
        setZoningRule(zoningRes.data);
        setZoningForm({
          ...zoningRes.data,
          permitted_uses: zoningRes.data.permitted_uses || [],
          max_height_meters: zoningRes.data.max_height_meters || '',
          max_height_storeys: zoningRes.data.max_height_storeys || '',
          front_setback: zoningRes.data.front_setback || '',
          side_setback_left: zoningRes.data.side_setback_left || '',
          side_setback_right: zoningRes.data.side_setback_right || '',
          rear_setback: zoningRes.data.rear_setback || '',
          lot_coverage_percent: zoningRes.data.lot_coverage_percent || '',
          floor_area_ratio: zoningRes.data.floor_area_ratio || '',
          parking_ratio: zoningRes.data.parking_ratio || '',
          special_constraints: zoningRes.data.special_constraints || '',
          notes: zoningRes.data.notes || ''
        });
        
        // Inject verified article references from backend
        if (zoningRes.data.article_references) {
          const refs = zoningRes.data.article_references;
          setZoningRule(prev => ({
            ...prev,
            regulatory_articles: {
              ...(prev?.regulatory_articles || {}),
              side_setback: refs.marge_laterale,
              rear_setback: refs.marge_arriere,
              parking_requirements: refs.stationnement,
              bicycle_parking: refs.stationnement_velos,
              max_units_h4: refs.logements_h4,
              floor_area_ratio: refs.densite_isp,
            }
          }));
        }
      } catch (e) {
        // Zoning data may not exist yet for new projects - this is expected
        console.debug('Zoning data not found for project:', id);
      }
      
      if (projectRes.data.status === 'analyzed') {
        setAnalysisResult({
          scenarios: projectRes.data.scenario_results || [],
          recommended_next_step: projectRes.data.recommended_next_step,
          financial_analysis: projectRes.data.financial_analysis || [],
          development_score: projectRes.data.development_score,
          neighborhood_context: projectRes.data.neighborhood_context,
          nearby_projects: projectRes.data.nearby_projects || [],
          sustainability: projectRes.data.sustainability
        });
      }
      
      const templatesRes = await adminAPI.listZoneTemplates();
      setZoneTemplates(templatesRes.data);
      
    } catch (error) {
      toast.error('Erreur de chargement du projet');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const handleZoningChange = (field, value) => {
    setZoningForm({ ...zoningForm, [field]: value });
  };

  const handleUsesChange = (value) => {
    const uses = value.split(',').map(u => u.trim()).filter(u => u);
    setZoningForm({ ...zoningForm, permitted_uses: uses });
  };

  const applyTemplate = (templateId) => {
    const template = zoneTemplates.find(t => t.id === templateId);
    if (template) {
      setZoningForm({
        ...zoningForm,
        permitted_uses: template.permitted_uses || [],
        max_height_meters: template.max_height_meters || '',
        max_height_storeys: template.max_height_storeys || '',
        front_setback: template.front_setback || '',
        side_setback_left: template.side_setback_left || '',
        side_setback_right: template.side_setback_right || '',
        rear_setback: template.rear_setback || '',
        lot_coverage_percent: template.lot_coverage_percent || '',
        floor_area_ratio: template.floor_area_ratio || '',
        parking_ratio: template.parking_ratio || ''
      });
      toast.success('Modèle appliqué');
    }
  };

  const saveZoningRules = async () => {
    setSaving(true);
    try {
      const data = {
        project_id: id,
        permitted_uses: zoningForm.permitted_uses,
        max_height_meters: zoningForm.max_height_meters ? parseFloat(zoningForm.max_height_meters) : null,
        max_height_storeys: zoningForm.max_height_storeys ? parseInt(zoningForm.max_height_storeys) : null,
        front_setback: zoningForm.front_setback ? parseFloat(zoningForm.front_setback) : null,
        side_setback_left: zoningForm.side_setback_left ? parseFloat(zoningForm.side_setback_left) : null,
        side_setback_right: zoningForm.side_setback_right ? parseFloat(zoningForm.side_setback_right) : null,
        rear_setback: zoningForm.rear_setback ? parseFloat(zoningForm.rear_setback) : null,
        lot_coverage_percent: zoningForm.lot_coverage_percent ? parseFloat(zoningForm.lot_coverage_percent) : null,
        floor_area_ratio: zoningForm.floor_area_ratio ? parseFloat(zoningForm.floor_area_ratio) : null,
        parking_ratio: zoningForm.parking_ratio || null,
        special_constraints: zoningForm.special_constraints || null,
        notes: zoningForm.notes || null
      };
      
      await zoningAPI.create(data);
      toast.success('Règles de zonage enregistrées');
      await loadProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await analysisAPI.run(id);
      setAnalysisResult(response.data);
      setActiveTab('overview');
      toast.success('Analyse terminée');
      await loadProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'analyse');
    } finally {
      setAnalyzing(false);
    }
  };

  const downloadReport = async () => {
    try {
      const response = await analysisAPI.getReport(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_zonepro_${project.name.replace(/\s+/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Rapport téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDelete = async () => {
    try {
      await projectsAPI.delete(id);
      toast.success('Projet supprimé');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const viewArticle = (article) => {
    setSelectedArticle(article);
    setShowArticleDialog(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat(language === 'fr' ? 'fr-CA' : 'en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(value);
  };

  if (loading) {
    return (
      <div className="p-8">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  const standardScenario = analysisResult?.scenarios?.find(s => s.scenario_type === 'standard');
  const standardFinancial = analysisResult?.financial_analysis?.find(f => f.scenario_type === 'standard');

  return (
    <div className="p-8" data-testid="project-detail">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-2" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </Button>
          <h1 className="font-heading font-bold text-2xl uppercase text-primary">{project.name}</h1>
          
          {/* Address display - always visible */}
          <div className="mt-2 space-y-1">
            {project.property?.address ? (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-accent" />
                <span className="text-base font-medium">{project.property.address}</span>
              </div>
            ) : project.property?.associated_addresses?.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                <MapPin className="w-4 h-4 text-accent flex-shrink-0" />
                <span className="text-base font-medium">
                  {project.property.associated_addresses[0].address_formatted}
                </span>
                {project.property.associated_addresses.length > 1 && (
                  <Badge variant="outline" className="text-xs">
                    +{project.property.associated_addresses.length - 1} {language === 'fr' ? 'autre' : 'other'}{project.property.associated_addresses.length > 2 ? 's' : ''}
                  </Badge>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span className="text-sm italic">{language === 'fr' ? 'Aucune adresse civique associée' : 'No civic address associated'}</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3 mt-2">
            <Badge variant={project.status === 'analyzed' ? 'default' : 'secondary'}>
              {project.status === 'analyzed' ? t('project.status.analyzed') : t('project.status.draft')}
            </Badge>
            {(project.property?.arrondissement || project.property?.municipality) && (
              <span className="text-muted-foreground text-sm">
                {[project.property.arrondissement, project.property.municipality].filter(Boolean).join(', ')}
              </span>
            )}
            {project.property?.cadastral_number && (
              <Badge variant="outline" className="font-mono text-xs">
                Lot {project.property.cadastral_number}
              </Badge>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {project.status === 'analyzed' && (
            <Button variant="outline" onClick={downloadReport} data-testid="download-report-btn">
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-destructive" data-testid="delete-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('project.actions.deleteConfirm')}</AlertDialogTitle>
                <AlertDialogDescription>{t('project.actions.deleteWarning')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-destructive">{t('common.delete')}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <BarChart3 className="w-4 h-4 mr-2" />
            {t('project.tabs.overview')}
          </TabsTrigger>
          <TabsTrigger value="regulatory" data-testid="tab-regulatory">
            <FileText className="w-4 h-4 mr-2" />
            {t('project.tabs.regulatory')}
          </TabsTrigger>
          <TabsTrigger value="financial" data-testid="tab-financial" className="opacity-60">
            <DollarSign className="w-4 h-4 mr-2" />
            {t('project.tabs.financial')}
            <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0">Bientôt</Badge>
          </TabsTrigger>
          <TabsTrigger value="scenarios" data-testid="tab-scenarios" className="opacity-60">
            <TrendingUp className="w-4 h-4 mr-2" />
            {t('project.tabs.scenarios')}
            <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0">Bientôt</Badge>
          </TabsTrigger>
          <TabsTrigger value="neighborhood" data-testid="tab-neighborhood" className="opacity-60">
            <Users className="w-4 h-4 mr-2" />
            {t('project.tabs.neighborhood')}
            <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0">Bientôt</Badge>
          </TabsTrigger>
          <TabsTrigger value="sustainability" data-testid="tab-sustainability" className="opacity-60">
            <Leaf className="w-4 h-4 mr-2" />
            {t('project.tabs.sustainability')}
            <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0">Bientôt</Badge>
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB - Regulatory Only */}
        <TabsContent value="overview">
          {isSteTherese && (
            <div className="space-y-4 mb-6">
              <Card className="border-l-4 border-l-accent" data-testid="st-overview-card">
                <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase text-muted-foreground">Ville de Sainte-Thérèse — Règlement de zonage 1200 N.S.</p>
                    <p className="text-2xl font-heading font-bold font-mono">Zone {project?.property?.zone_code}</p>
                    {project?.property?.lot_area ? (
                      <p className="text-sm mt-1" data-testid="st-overview-lot-area">
                        Surface du lot : <span className="font-bold">{project.property.lot_area.toLocaleString('fr-CA')} m²</span>
                        {project?.property?.cadastral_number && <> — Lot {project.property.cadastral_number} (Cadastre du Québec)</>}
                      </p>
                    ) : null}
                    <p className="text-sm text-muted-foreground mt-1">
                      {language === 'fr'
                        ? "Les normes officielles de cette zone sont disponibles dans l'onglet Réglementaire. L'analyse de scénarios n'est pas encore offerte pour cette juridiction."
                        : 'Official standards for this zone are available in the Regulatory tab. Scenario analysis is not yet offered for this jurisdiction.'}
                    </p>
                  </div>
                  <Button onClick={() => setActiveTab('regulatory')} className="bg-accent text-primary hover:bg-accent/90" data-testid="st-goto-regulatory-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Voir la grille des usages et normes' : 'View uses and standards grid'}
                  </Button>
                </CardContent>
              </Card>
              <StProvenanceBanner property={project?.property} />
            </div>
          )}
          {readZone && (
            <div className="mb-6">
              <ZoneOutlinesWarning property={project.property} />
              <ReadZoneSheet territoireId={project.property.jurisdiction_id} zoneCode={project.property.zone_code} />
            </div>
          )}
          {project && !zoningRule && !project?.property?.sector_number && !isSteTherese && !readZone && (
            <Alert variant="destructive" className="mb-6" data-testid="no-regulatory-data-alert">
              <AlertTitle>Aucune donnée réglementaire disponible</AlertTitle>
              <AlertDescription>
                Le secteur de zonage n&apos;a pas été confirmé pour ce projet. Aucune norme n&apos;est affichée tant que le secteur n&apos;est pas déterminé.
              </AlertDescription>
            </Alert>
          )}
          {analysisResult && (
            <div className="space-y-6">
              {/* Regulatory Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-accent/10 flex items-center justify-center">
                        <Building className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Hauteur maximale</p>
                        <p className="text-2xl font-heading font-bold">
                          {analysisResult.regulatory_summary?.max_height || zoningRule?.max_height_storeys || analysisResult.zoning_rule?.max_height_storeys || '-'} étages
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-accent/10 flex items-center justify-center">
                        <Layers className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Taux d&apos;implantation</p>
                        <p className="text-2xl font-heading font-bold">
                          {analysisResult.regulatory_summary?.lot_coverage 
                            ? `${(analysisResult.regulatory_summary.lot_coverage * 100).toFixed(0)}%`
                            : (zoningRule?.lot_coverage_percent || analysisResult.zoning_rule?.lot_coverage_percent)
                              ? `${zoningRule?.lot_coverage_percent || analysisResult.zoning_rule?.lot_coverage_percent}%`
                              : '-'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-accent/10 flex items-center justify-center">
                        <Ruler className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Surface du lot</p>
                        <p className="text-2xl font-heading font-bold">
                          {project?.property?.lot_area?.toLocaleString() || '-'} m²
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {sheetHasNoDimensionalNorms(sectorSheet) && (
                <Alert className="border-blue-200 bg-blue-50" data-testid="no-norms-institutional-note">
                  <HelpCircle className="h-4 w-4 text-blue-600" />
                  <AlertTitle className="text-blue-900">Aucune norme dimensionnelle prescrite pour ce secteur</AlertTitle>
                  <AlertDescription className="text-blue-800">
                    La fiche officielle du secteur {project?.property?.sector_number} (zone {project?.property?.zone_code}) ne prescrit
                    aucune hauteur, densité, taux d&apos;implantation ni marge. C&apos;est le cas des zones d&apos;équipements collectifs
                    et de parcs (E.1) — ce n&apos;est pas un défaut de l&apos;application. Pour tout projet dans ce secteur,
                    adressez-vous à l&apos;arrondissement Rosemont–La Petite-Patrie.
                  </AlertDescription>
                </Alert>
              )}

              {project?.property?.coproperty?.is_coproperty && (
                <Alert className="border-indigo-200 bg-indigo-50" data-testid="coproperty-banner">
                  <Building className="h-4 w-4 text-indigo-600" />
                  <AlertTitle className="text-indigo-900">
                    Copropriété divise — {project.property.coproperty.total_fractions} fractions à cette adresse, dont {project.property.coproperty.total_logements} logements
                  </AlertTitle>
                  <AlertDescription className="text-indigo-800">
                    Les normes de zonage s&apos;appliquent identiquement à toutes les unités. La superficie affichée est
                    celle du lot cadastral complet (et non la quote-part d&apos;une fraction).
                    {(project.property.coproperty.parking_fractions > 0 || project.property.coproperty.storage_fractions > 0) && (
                      <> Fractions non résidentielles : {[
                        project.property.coproperty.parking_fractions > 0 ? `${project.property.coproperty.parking_fractions} stationnements intérieurs` : null,
                        project.property.coproperty.storage_fractions > 0 ? `${project.property.coproperty.storage_fractions} espaces de rangement` : null,
                      ].filter(Boolean).join(' · ')}.</>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* Zoning Info Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading uppercase text-base flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Informations de zonage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                    <div>
                      <p className="text-xs text-muted-foreground">Zone</p>
                      <p className="text-lg font-bold">{project?.property?.zone_code || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Arrondissement</p>
                      <p className="text-lg font-bold" data-testid="zoning-arrondissement">{project?.property?.arrondissement || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Ville</p>
                      <p className="text-lg font-bold" data-testid="zoning-city">{project?.property?.municipality || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Marge avant</p>
                      {(zoningRule?.front_setback || analysisResult.zoning_rule?.front_setback) ? (
                        <p className="text-lg font-bold">{zoningRule?.front_setback || analysisResult.zoning_rule?.front_setback} m</p>
                      ) : (
                        <p className="text-sm font-medium leading-snug" data-testid="front-setback-summary">
                          Non prescrite — alignement des bâtiments voisins
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Marge arrière</p>
                      <p className="text-lg font-bold">{zoningRule?.rear_setback || analysisResult.zoning_rule?.rear_setback || '-'} m</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Coming Soon Banner for Financial/Scenarios */}
              <Card className="border-dashed border-2 bg-muted/20">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-heading font-bold">Analyses financières et scénarios</h4>
                      <p className="text-sm text-muted-foreground">
                        Les projections financières, scénarios de développement et analyses de quartier seront bientôt disponibles.
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-sm">Bientôt disponible</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Validation Items */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading uppercase text-base flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-muted-foreground" />
                    Éléments à valider avec l&apos;arrondissement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm">
                      <HelpCircle className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      Confirmer le zonage applicable auprès de l&apos;arrondissement
                    </li>
                    <li className="flex items-start gap-2 text-sm">
                      <HelpCircle className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      Vérifier les contraintes spécifiques au site (patrimoine, environnement)
                    </li>
                    <li className="flex items-start gap-2 text-sm">
                      <HelpCircle className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      Valider les marges et retraits avec un professionnel
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* FINANCIAL TAB - Coming Soon */}
        <TabsContent value="financial">
          <ComingSoonTab 
            title="Analyses financières"
            description="Les projections de coûts, revenus et rentabilité seront bientôt disponibles pour vous aider à évaluer la faisabilité économique de votre projet."
            icon={DollarSign}
          />
        </TabsContent>

        {/* SCENARIOS TAB - Coming Soon */}
        <TabsContent value="scenarios">
          <ComingSoonTab 
            title="Scénarios de développement"
            description="Comparez différents scénarios de développement (conservateur, standard, optimisé) avec des projections détaillées pour chaque option."
            icon={TrendingUp}
          />
        </TabsContent>

        {/* NEIGHBORHOOD TAB - Coming Soon */}
        <TabsContent value="neighborhood">
          <ComingSoonTab 
            title="Contexte du quartier"
            description="Découvrez les caractéristiques du secteur, les projets similaires récents et les tendances de développement dans votre arrondissement."
            icon={Users}
          />
        </TabsContent>

        {/* SUSTAINABILITY TAB - Coming Soon */}
        <TabsContent value="sustainability">
          <ComingSoonTab 
            title="Analyse de durabilité"
            description="Évaluez le potentiel de certifications écologiques, les incitatifs disponibles et les économies d'énergie possibles pour votre projet."
            icon={Leaf}
          />
        </TabsContent>

        {/* REGULATORY TAB - Enhanced with all cadastral, location, and zoning data */}
        <TabsContent value="regulatory">
          {isSteTherese ? (
            <div className="space-y-6" data-testid="st-regulatory-tab">
              {/* En-tête — même structure que Montréal */}
              <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-4 -mx-4 px-4 -mt-4 pt-4 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-lg font-bold uppercase">Données Réglementaires</h2>
                    <p className="text-sm text-muted-foreground">
                      Zone: <span className="font-mono font-bold">{project?.property?.zone_code || '—'}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-3 px-4 py-2 rounded-lg border bg-green-50 border-green-200">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <div className="text-right">
                      <p className="font-medium text-sm">Réglementaire</p>
                      <p className="text-xs text-muted-foreground">
                        {project?.property?.zoning_data_source || 'Règlement de zonage 1200 N.S.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <StProvenanceBanner property={project?.property} />

              {(project?.property?.cadastral_number || project?.property?.lot_area) ? (
                <StCadastralCard property={project?.property} />
              ) : (
                <Alert data-testid="st-no-lot-note">
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Aucun lot associé — ce projet a été créé par saisie manuelle du code de zone (repli).
                    Recréez l&apos;analyse par adresse ou par numéro de lot pour obtenir les dimensions du terrain.
                  </AlertDescription>
                </Alert>
              )}

              <StLocationCard property={project?.property} />
              <StPropertyProfileCard />

              <SteThereseZoneSheet zoneCode={project?.property?.zone_code} language={language} />
            </div>
          ) : (
          <div className="space-y-6">
            {/* Sticky Header with Data Source Indicator - Top Right */}
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-4 -mx-4 px-4 -mt-4 pt-4 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-lg font-bold uppercase">Données Réglementaires</h2>
                  <p className="text-sm text-muted-foreground">
                    Zone: <span className="font-mono font-bold">{project?.property?.zone_code || zoningRule?.zone_code_source || '—'}</span>
                  </p>
                </div>
                
                {/* Data Source Badge - Top Right */}
                <div className={`flex items-center gap-3 px-4 py-2 rounded-lg border ${
                  project?.property?.zoning_data_type === 'regulatory' 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-orange-50 border-orange-200'
                }`}>
                  {project?.property?.zoning_data_type === 'regulatory' ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                  )}
                  <div className="text-right">
                    <p className="font-medium text-sm">
                      {project?.property?.zoning_data_type === 'regulatory' 
                        ? 'Réglementaire' 
                        : 'Planification'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {project?.property?.zoning_data_source || 'Plan d\'urbanisme'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Status Legend */}
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span> Confirmé
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span> Estimé
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-gray-400"></span> Hypothèse
                </span>
              </div>
            </div>

            {/* Cadastral Data Section */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading uppercase flex items-center gap-2">
                  <FileText className="w-5 h-5 text-accent" />
                  Données cadastrales
                </CardTitle>
                <CardDescription className="flex items-center gap-2">
                  Informations du lot issues du Cadastre du Québec (MERN)
                  <span className="w-2 h-2 rounded-full bg-green-500" title="Confirmé"></span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Numéro de lot</p>
                      <span className="w-2 h-2 rounded-full bg-green-500" title="Confirmé - Cadastre du Québec"></span>
                    </div>
                    <p className="font-mono text-xl font-bold text-primary">
                      {project?.property?.cadastral_number || '—'}
                    </p>
                    <p className="text-xs text-muted-foreground">Cadastre du Québec</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Superficie</p>
                      <span className="w-2 h-2 rounded-full bg-green-500" title="Confirmé - Géométrie cadastrale"></span>
                    </div>
                    <p className="text-xl font-bold">{project?.property?.lot_area?.toFixed(1) || '—'} m²</p>
                    <p className="text-xs text-muted-foreground">Géométrie cadastrale</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Largeur (façade)</p>
                      <span className={`w-2 h-2 rounded-full ${
                        project?.property?.dimension_confidence === 'high' ? 'bg-green-500' :
                        project?.property?.dimension_confidence === 'medium' ? 'bg-yellow-500' : 'bg-gray-400'
                      }`} title={project?.property?.dimension_confidence === 'high' ? 'Fiable' : 
                        project?.property?.dimension_confidence === 'medium' ? 'Moyenne' : 'Approximative'}></span>
                    </div>
                    <p className="text-xl font-bold">{project?.property?.lot_width?.toFixed(2) || '—'} m</p>
                    <p className="text-xs text-muted-foreground">Calcul MBR</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Profondeur</p>
                      <span className={`w-2 h-2 rounded-full ${
                        project?.property?.dimension_confidence === 'high' ? 'bg-green-500' :
                        project?.property?.dimension_confidence === 'medium' ? 'bg-yellow-500' : 'bg-gray-400'
                      }`} title={project?.property?.dimension_confidence === 'high' ? 'Fiable' : 
                        project?.property?.dimension_confidence === 'medium' ? 'Moyenne' : 'Approximative'}></span>
                    </div>
                    <p className="text-xl font-bold">{project?.property?.lot_depth?.toFixed(2) || '—'} m</p>
                    <p className="text-xs text-muted-foreground">Calcul MBR</p>
                  </div>
                </div>
                
                {/* Warning for condo lots (PC-xxxxx) - geometry represents the "lot-mère" */}
                {project?.property?.cadastral_number?.toUpperCase().startsWith('PC-') && (
                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800 dark:text-amber-300">
                        Lot de copropriété (condo)
                      </p>
                      <p className="text-amber-700 dark:text-amber-400/80 mt-1">
                        Les dimensions et la superficie affichées représentent le <strong>lot-mère</strong> (terrain complet de l&apos;immeuble), 
                        et non l&apos;unité de condo individuelle. Pour une analyse précise, veuillez consulter la déclaration de copropriété.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Location Section */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading uppercase flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-accent" />
                  Localisation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Arrondissement</p>
                      <span className="w-2 h-2 rounded-full bg-green-500" title="Confirmé - Reverse geocoding"></span>
                    </div>
                    <p className="text-lg font-semibold">{project?.property?.arrondissement || '—'}</p>
                    <p className="text-xs text-muted-foreground">Reverse geocoding du centroïde</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Coordonnées (centroïde)</p>
                      <span className="w-2 h-2 rounded-full bg-green-500" title="Confirmé - Géométrie cadastrale"></span>
                    </div>
                    <p className="font-mono text-sm">
                      {project?.property?.latitude?.toFixed(6) || '—'}, {project?.property?.longitude?.toFixed(6) || '—'}
                    </p>
                    <p className="text-xs text-muted-foreground">Centroïde de la géométrie</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Building Detection Status Section */}
            {analysisResult?.building_detection && (
              <Card className={analysisResult.building_detection.has_existing_building 
                ? "border-amber-200 bg-amber-50/30" 
                : "border-green-200 bg-green-50/30"}>
                <CardHeader>
                  <CardTitle className="font-heading uppercase flex items-center gap-2">
                    {analysisResult.building_detection.has_existing_building ? (
                      <>
                        <Building className="w-5 h-5 text-amber-600" />
                        Bâtiment existant détecté
                      </>
                    ) : (
                      <>
                        <Layers className="w-5 h-5 text-green-600" />
                        Terrain vacant
                      </>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Données du rôle d&apos;évaluation foncière de Montréal
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {analysisResult.building_detection.has_existing_building ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {analysisResult.building_detection.superficie_batiment && (
                          <div className="text-center p-3 bg-white rounded-lg">
                            <p className="text-xs text-muted-foreground uppercase">{project?.property?.coproperty?.is_coproperty ? 'Superficie bâtiment (fraction)' : 'Superficie bâtiment'}</p>
                            <p className="text-lg font-bold">{analysisResult.building_detection.superficie_batiment.toFixed(0)} m²</p>
                          </div>
                        )}
                        {analysisResult.building_detection.etages && (
                          <div className="text-center p-3 bg-white rounded-lg">
                            <p className="text-xs text-muted-foreground uppercase">Étages</p>
                            <p className="text-lg font-bold">{analysisResult.building_detection.etages}</p>
                          </div>
                        )}
                        {(project?.property?.coproperty?.is_coproperty || analysisResult.building_detection.nombre_logements) && (
                          <div className="text-center p-3 bg-white rounded-lg" data-testid="building-logements-stat">
                            <p className="text-xs text-muted-foreground uppercase">{project?.property?.coproperty?.is_coproperty ? 'Logements (immeuble)' : 'Logements'}</p>
                            <p className="text-lg font-bold">{project?.property?.coproperty?.is_coproperty ? project.property.coproperty.total_logements : analysisResult.building_detection.nombre_logements}</p>
                          </div>
                        )}
                        {analysisResult.building_detection.annee_construction && (
                          <div className="text-center p-3 bg-white rounded-lg">
                            <p className="text-xs text-muted-foreground uppercase">Année construction</p>
                            <p className="text-lg font-bold">{analysisResult.building_detection.annee_construction}</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Asbestos Risk Warning */}
                      {analysisResult.building_detection.is_pre_1990 && (
                        <div className="p-3 bg-amber-100 border border-amber-300 rounded-lg flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-amber-800">Risque d&apos;amiante potentiel</p>
                            <p className="text-sm text-amber-700">
                              Bâtiment construit avant 1990 — prévoir des coûts de désamiantage additionnels 
                              non inclus dans les estimations financières.
                            </p>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground">
                        Les scénarios présentés sont adaptés à la présence d&apos;un bâtiment existant: 
                        surélévation, démolition-reconstruction, ou rénovation.
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="font-medium">Terrain disponible pour construction neuve</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        Les scénarios présentés sont optimisés pour une construction neuve.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Associated Addresses Section */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading uppercase flex items-center gap-2">
                  <Home className="w-5 h-5 text-accent" />
                  Adresses associées au lot
                </CardTitle>
                <CardDescription className="flex items-center gap-2">
                  Adresses civiques du rôle d&apos;évaluation foncière de Montréal
                  {project?.property?.associated_addresses?.length > 0 ? (
                    <span className="w-2 h-2 rounded-full bg-green-500" title="Confirmé - Rôle d'évaluation"></span>
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-gray-400" title="Non disponible"></span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {project?.property?.associated_addresses?.length > 0 ? (
                  <div className="space-y-2">
                    {project.property.associated_addresses.map((addr, idx) => (
                      <div key={addr.address_formatted || `addr-${idx}`} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg" data-testid="associated-address">
                        <div className="flex items-center gap-3">
                          <MapPin className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <span className="font-medium">{addr.address_formatted || 'Adresse non formatée'}</span>
                            {addr.units > 1 && (
                              <span className="ml-2 text-xs text-muted-foreground" data-testid="associated-address-units">{addr.units} unités</span>
                            )}
                            {addr.source && (
                              <span className="ml-2 text-xs text-muted-foreground">({addr.source})</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {addr.confidence && (
                            <span className={`w-2 h-2 rounded-full ${
                              addr.confidence === 'high' ? 'bg-green-500' :
                              addr.confidence === 'medium' ? 'bg-yellow-500' : 'bg-gray-400'
                            }`} title={`Confiance: ${addr.confidence}`}></span>
                          )}
                          {addr.superficie_terrain && typeof addr.superficie_terrain === 'number' && (
                            <Badge variant="outline" className="text-xs font-mono">
                              {addr.superficie_terrain.toFixed(1)} m²{project?.property?.coproperty?.is_coproperty ? ' (quote-part)' : ''}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <Home className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Aucune adresse civique trouvée dans le rôle d&apos;évaluation</p>
                    <p className="text-sm">Le lot peut être vacant ou les données non disponibles</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Zoning Rules Section: a zone read from its fiche shows its normes table, as the Résumé does (fix 144, P4 = B) */}
            {readZone ? (
              <div data-testid="regulatory-read-zone">
                <ZoneOutlinesWarning property={project.property} />
                <ReadZoneSheet territoireId={project.property.jurisdiction_id} zoneCode={project.property.zone_code} />
              </div>
            ) : (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading uppercase flex items-center gap-2">
                  <Building className="w-5 h-5 text-accent" />
                  Règles de zonage
                </CardTitle>
                <CardDescription className="flex flex-col gap-1">
                  <span>
                    Zone: <span className="font-mono font-bold">{project?.property?.zone_code || zoningRule?.zone_code_source || '—'}</span>
                    {zoningRule?.regulatory_reference && (
                      <span className="ml-2 text-xs">• {zoningRule.regulatory_reference}</span>
                    )}
                  </span>
                  {/* Source link to official PDF */}
                  {project?.property?.sector_number && (
                    <a 
                      href={`https://www1.ville.montreal.qc.ca/CartesInteractives/rpp/doc/RPP_Zone${project.property.sector_number.padStart(4, '0')}.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                    >
                      <FileText className="w-3 h-3" />
                      Source: Fiche officielle Ville de Montréal (secteur {project.property.sector_number})
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {sheetHasNoDimensionalNorms(sectorSheet) && (
                    <Alert className="border-blue-200 bg-blue-50" data-testid="no-norms-institutional-note-reg">
                      <HelpCircle className="h-4 w-4 text-blue-600" />
                      <AlertTitle className="text-blue-900">Aucune norme dimensionnelle prescrite pour ce secteur</AlertTitle>
                      <AlertDescription className="text-blue-800">
                        La fiche officielle ne prescrit aucune hauteur, densité, taux d&apos;implantation ni marge pour cette zone
                        ({project?.property?.zone_code}) — typique des équipements collectifs et parcs (E.1). Ce n&apos;est pas un défaut
                        de l&apos;application : adressez-vous à l&apos;arrondissement Rosemont–La Petite-Patrie pour tout projet ici.
                      </AlertDescription>
                    </Alert>
                  )}
                  {/* Permitted Uses */}
                  <div className="pb-4 border-b">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">Usages permis</p>
                        <NormeDot dot={RULE_DOTS.PERMITTED_USES} states={zoningRule?.norme_states} />
                      </div>
                      {zoningRule?.regulatory_articles?.permitted_uses ? (
                        <button 
                          onClick={() => viewArticle(zoningRule.regulatory_articles.permitted_uses)}
                          className="text-xs bg-accent/10 text-accent hover:bg-accent/20 px-2 py-1 rounded font-medium"
                        >
                          → {zoningRule.regulatory_articles.permitted_uses.article}
                        </button>
                      ) : project?.property?.zoning_data_type !== 'regulatory' && (
                        <span className="text-xs text-muted-foreground bg-gray-100 px-2 py-1 rounded">Plan d&apos;urbanisme</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(zoningForm.permitted_uses?.length > 0 ? zoningForm.permitted_uses : ['Non spécifié']).map((use) => (
                        <Badge key={use} variant="outline" className="bg-green-50 text-green-700 border-green-200" data-testid="permitted-use">
                          {sectorSheet?.usage_display?.[use] ?? use}
                        </Badge>
                      ))}
                    </div>
                    {sectorSheet && (
                      <p className="mt-2 text-xs text-muted-foreground" data-testid="sector-sheet-autres-usages">
                        Autres usages : <span className="font-medium text-foreground">{usagesVal(sectorSheet.autres_usages, sectorSheet.usage_display)}</span>
                      </p>
                    )}
                    {/* Article 139 - H.4 category ONLY (4-8 logements) */}
                    {/* Condition: zone_code starts with "H.4" or contains "H.4-" (e.g., H.4-6, H.4-7) */}
                    {zoningRule?.regulatory_articles?.max_units_h4 && 
                     (project?.property?.zone_code?.startsWith('H.4') || 
                      project?.property?.zone_code?.includes('H.4-') ||
                      zoningForm?.permitted_uses?.some(use => use.toLowerCase().includes('4 à 8 logements'))) && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                        <span>Normes de densité catégorie H.4 :</span>
                        <button 
                          onClick={() => viewArticle(zoningRule.regulatory_articles.max_units_h4)}
                          className="bg-accent/10 text-accent hover:bg-accent/20 px-2 py-1 rounded font-medium"
                        >
                          {zoningRule.regulatory_articles.max_units_h4.article}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Building Parameters */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Max Height */}
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1">
                          <p className="text-xs text-muted-foreground">{sectorSheet ? 'Hauteur (min – max)' : 'Hauteur max'}</p>
                          <NormeDot dot={RULE_DOTS.MAX_STOREYS} states={zoningRule?.norme_states} />
                        </div>
                      </div>
                      {sectorSheet?.dimensional_standards ? (
                        <div className="space-y-0.5" data-testid="height-min-max">
                          <p className="text-sm">Étages : <span className="font-bold">{ficheVal(sectorSheet.dimensional_standards.hauteur_etage?.min)} – {ficheVal(sectorSheet.dimensional_standards.hauteur_etage?.max)}</span></p>
                          <p className="text-sm">Mètres : <span className="font-bold">{ficheVal(sectorSheet.dimensional_standards.hauteur_m?.min)} – {ficheVal(sectorSheet.dimensional_standards.hauteur_m?.max)}</span></p>
                        </div>
                      ) : (
                        <>
                          <p className="text-lg font-bold">{zoningForm.max_height_storeys || '—'} étages</p>
                          <p className="text-sm text-muted-foreground">{zoningForm.max_height_meters || '—'} m</p>
                        </>
                      )}
                      {zoningRule?.regulatory_articles?.max_storeys ? (
                        <button 
                          onClick={() => viewArticle(zoningRule.regulatory_articles.max_storeys)}
                          className="mt-2 text-xs bg-accent/10 text-accent hover:bg-accent/20 px-2 py-1 rounded font-medium w-full text-center"
                        >
                          {zoningRule.regulatory_articles.max_storeys.article}
                        </button>
                      ) : project?.property?.zoning_data_type !== 'regulatory' && (
                        <span className="mt-2 block text-xs text-muted-foreground bg-gray-100 px-2 py-1 rounded text-center">PU</span>
                      )}
                    </div>
                    
                    {/* Lot Coverage */}
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1">
                          <p className="text-xs text-muted-foreground">{sectorSheet ? "Taux d'implantation (min – max)" : "Taux d'implantation"}</p>
                          <NormeDot dot={RULE_DOTS.LOT_COVERAGE} states={zoningRule?.norme_states} />
                        </div>
                      </div>
                      {sectorSheet?.dimensional_standards ? (
                        <p className="text-lg font-bold" data-testid="coverage-min-max">{ficheVal(sectorSheet.dimensional_standards.taux_implantation_pct?.min, ' %')} – {ficheVal(sectorSheet.dimensional_standards.taux_implantation_pct?.max, ' %')}</p>
                      ) : (
                        <p className="text-lg font-bold">{zoningForm.lot_coverage_percent || '—'}%</p>
                      )}
                      {zoningRule?.regulatory_articles?.lot_coverage ? (
                        <button 
                          onClick={() => viewArticle(zoningRule.regulatory_articles.lot_coverage)}
                          className="mt-2 text-xs bg-accent/10 text-accent hover:bg-accent/20 px-2 py-1 rounded font-medium w-full text-center"
                        >
                          {zoningRule.regulatory_articles.lot_coverage.article}
                        </button>
                      ) : project?.property?.zoning_data_type !== 'regulatory' && (
                        <span className="mt-2 block text-xs text-muted-foreground bg-gray-100 px-2 py-1 rounded text-center">PU</span>
                      )}
                    </div>
                    
                    {/* Densité (ISP) — article 35 du Règlement 01-279 */}
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1">
                          <p className="text-xs text-muted-foreground">Densité (ISP)</p>
                          <NormeDot dot={RULE_DOTS.FLOOR_AREA_RATIO} states={zoningRule?.norme_states} />
                        </div>
                      </div>
                      {sectorSheet?.dimensional_standards ? (
                        <div className="space-y-0.5" data-testid="density-min-max">
                          <p className="text-sm">Min : <span className="font-bold">{ficheVal(sectorSheet.dimensional_standards.densite?.min)}</span></p>
                          <p className="text-sm">Max : <span className="font-bold">{ficheVal(sectorSheet.dimensional_standards.densite?.max)}</span></p>
                        </div>
                      ) : (
                        <p className="text-lg font-bold">{zoningForm.floor_area_ratio || '—'}</p>
                      )}
                      {zoningRule?.regulatory_articles?.floor_area_ratio ? (
                        <button 
                          onClick={() => viewArticle(zoningRule.regulatory_articles.floor_area_ratio)}
                          className="mt-2 text-xs bg-accent/10 text-accent hover:bg-accent/20 px-2 py-1 rounded font-medium w-full text-center"
                          data-testid="article-35-badge"
                        >
                          Article {zoningRule.regulatory_articles.floor_area_ratio.article}
                        </button>
                      ) : project?.property?.zoning_data_type !== 'regulatory' && (
                        <span className="mt-2 block text-xs text-muted-foreground bg-gray-100 px-2 py-1 rounded text-center">PU</span>
                      )}
                    </div>
                    
                    {/* Parking */}
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1">
                          <p className="text-xs text-muted-foreground">Stationnement</p>
                          <NormeDot />
                        </div>
                      </div>
                      <p className="text-sm font-medium">{zoningForm.parking_ratio || 'Non spécifié'}</p>
                      {zoningRule?.regulatory_articles?.parking_requirements ? (
                        <button 
                          onClick={() => viewArticle(zoningRule.regulatory_articles.parking_requirements)}
                          className="mt-2 text-xs bg-accent/10 text-accent hover:bg-accent/20 px-2 py-1 rounded font-medium w-full text-center"
                        >
                          {zoningRule.regulatory_articles.parking_requirements.article}
                        </button>
                      ) : project?.property?.zoning_data_type !== 'regulatory' && (
                        <span className="mt-2 block text-xs text-muted-foreground bg-gray-100 px-2 py-1 rounded text-center">PU</span>
                      )}
                    </div>
                    
                    {/* Bicycle parking with Article 614 */}
                    {zoningRule?.regulatory_articles?.bicycle_parking && (
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-1 mb-1">
                          <p className="text-xs text-muted-foreground">Stationnement vélos</p>
                          <span className="w-2 h-2 rounded-full bg-green-500" title="Norme générale"></span>
                        </div>
                        <p className="text-sm font-medium">{zoningForm.bicycle_parking || 'Voir règlement'}</p>
                        <button 
                          onClick={() => viewArticle(zoningRule.regulatory_articles.bicycle_parking)}
                          className="mt-2 text-xs bg-accent/10 text-accent hover:bg-accent/20 px-2 py-1 rounded font-medium w-full text-center"
                        >
                          {zoningRule.regulatory_articles.bicycle_parking.article}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Fiche officielle du secteur — données complètes */}
                  {sectorSheet && (
                    <div className="pt-4 border-t" data-testid="sector-sheet-section">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <p className="text-sm font-medium">Fiche officielle du secteur {sectorSheet.sector_number}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {sectorSheet.reglement_citation && (
                            <span data-testid="sector-sheet-reglement">{sectorSheet.reglement_citation}</span>
                          )}
                          {sectorSheet.extraction_date && (
                            <span data-testid="sector-sheet-date">
                              Fiche extraite le {new Date(sectorSheet.extraction_date).toLocaleDateString('fr-CA')}
                            </span>
                          )}
                          {sectorSheet.source_url && (
                            <a
                              href={sectorSheet.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-accent hover:underline"
                              data-testid="sector-sheet-pdf-link"
                            >
                              <FileText className="w-3 h-3" /> PDF officiel <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-3 bg-muted/50 rounded-lg" data-testid="sector-sheet-mode">
                          <p className="text-xs text-muted-foreground mb-1">Mode d&apos;implantation</p>
                          <p className="text-sm font-bold">{modeImplantationLabel(sectorSheet.mode_implantation)}</p>
                        </div>
                        <div className="p-3 bg-muted/50 rounded-lg" data-testid="sector-sheet-verdissement">
                          <p className="text-xs text-muted-foreground mb-1">Verdissement min</p>
                          <p className="text-sm font-bold">{ficheVal(sectorSheet.verdissement_pct, ' %')}</p>
                        </div>
                        <div className="p-3 bg-muted/50 rounded-lg" data-testid="sector-sheet-patrimoine">
                          <p className="text-xs text-muted-foreground mb-1">Patrimoine</p>
                          <p className="text-sm font-bold">{ficheVal(sectorSheet.patrimoine)}</p>
                        </div>
                        <div className="p-3 bg-muted/50 rounded-lg" data-testid="sector-sheet-district">
                          <p className="text-xs text-muted-foreground mb-1">District électoral</p>
                          <p className="text-sm font-bold">{sectorSheet.district_electoral || '—'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Setbacks */}
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">Marges de recul</p>
                        <NormeDot dot={RULE_DOTS.SETBACKS} states={zoningRule?.norme_states} />
                      </div>
                      {!zoningRule?.regulatory_articles?.side_setback && project?.property?.zoning_data_type !== 'regulatory' && (
                        <span className="text-xs text-muted-foreground bg-gray-100 px-2 py-1 rounded">Plan d&apos;urbanisme</span>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="p-2 bg-muted/30 rounded">
                        <p className="text-xs text-muted-foreground">Avant</p>
                        {zoningForm.front_setback ? (
                          <p className="font-bold">{zoningForm.front_setback} m</p>
                        ) : (
                          <p className="text-xs mt-1 text-muted-foreground leading-snug" data-testid="front-setback-explanation">
                            {"Non prescrite — dépend de l'alignement de construction des bâtiments voisins (Annexe G, Fascicule d'évaluation 1, critère « Alignement »)"}
                          </p>
                        )}
                      </div>
                      <div className="p-2 bg-muted/30 rounded relative">
                        <p className="text-xs text-muted-foreground">Arrière</p>
                        <p className="font-bold">{zoningForm.rear_setback || '—'} m</p>
                        {zoningRule?.regulatory_articles?.rear_setback && (
                          <button 
                            onClick={() => viewArticle(zoningRule.regulatory_articles.rear_setback)}
                            className="absolute top-1 right-1 text-[10px] bg-accent/10 text-accent hover:bg-accent/20 px-1.5 py-0.5 rounded font-medium"
                            data-testid="rear-setback-article-btn"
                          >
                            {zoningRule.regulatory_articles.rear_setback.article}
                          </button>
                        )}
                      </div>
                      <div className="p-2 bg-muted/30 rounded relative">
                        <p className="text-xs text-muted-foreground">Latérale G</p>
                        <p className="font-bold">{zoningForm.side_setback_left || '—'} m</p>
                        {zoningRule?.regulatory_articles?.side_setback && (
                          <button 
                            onClick={() => viewArticle(zoningRule.regulatory_articles.side_setback)}
                            className="absolute top-1 right-1 text-[10px] bg-accent/10 text-accent hover:bg-accent/20 px-1.5 py-0.5 rounded font-medium"
                            data-testid="side-setback-left-article-btn"
                          >
                            {zoningRule.regulatory_articles.side_setback.article}
                          </button>
                        )}
                      </div>
                      <div className="p-2 bg-muted/30 rounded relative">
                        <p className="text-xs text-muted-foreground">Latérale D</p>
                        <p className="font-bold">{zoningForm.side_setback_right || '—'} m</p>
                        {zoningRule?.regulatory_articles?.side_setback && (
                          <button 
                            onClick={() => viewArticle(zoningRule.regulatory_articles.side_setback)}
                            className="absolute top-1 right-1 text-[10px] bg-accent/10 text-accent hover:bg-accent/20 px-1.5 py-0.5 rounded font-medium"
                            data-testid="side-setback-right-article-btn"
                          >
                            {zoningRule.regulatory_articles.side_setback.article}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Special Constraints */}
                  {zoningForm.special_constraints && (
                    <div className="pt-4 border-t">
                      <p className="text-sm font-medium mb-2">Contraintes spéciales</p>
                      <p className="text-sm text-muted-foreground">{zoningForm.special_constraints}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            )}
          </div>
          )}
        </TabsContent>

      </Tabs>

      {/* Article Dialog */}
      <Dialog open={showArticleDialog} onOpenChange={setShowArticleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">{selectedArticle?.article || selectedArticle?.number}</DialogTitle>
            <DialogDescription>{selectedArticle?.title || selectedArticle?.source || 'Extrait du règlement d\u2019urbanisme 01-279'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">Extrait réglementaire</p>
              <p className="mt-2 p-4 bg-muted rounded text-sm italic">&quot;{selectedArticle?.excerpt}&quot;</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Source</p>
              <p className="font-medium" data-testid="article-source">{selectedArticle?.source}</p>
            </div>
            {selectedArticle?.url && (
              <a 
                href={selectedArticle.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || ''}${selectedArticle.url}` : selectedArticle.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 text-accent rounded-lg hover:bg-accent/20 transition-colors"
              >
                <FileText className="w-4 h-4" />
                {selectedArticle?.page ? (
                  <span>Voir dans le règlement (page {selectedArticle.page})</span>
                ) : (
                  <span>Consulter le règlement complet</span>
                )}
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
            {selectedArticle?.verified && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Page vérifiée dans la codification administrative
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
